import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { QuantityOrOptionPicker } from "@faire/retailer-visitor-shared/components/QuantityOrOptionPicker/QuantityOrOptionPicker";
import { isDiscontinued as isProductOptionDiscontinued, isProductOptionInStock } from "@faire/retailer-visitor-shared/lib/productOptions";
import { buildCaseQuantityText } from "@faire/retailer-visitor-shared/views/product/ProductDetails/buildCaseQuantityText";
import { getProductDetails } from "@faire/retailer-visitor-shared/views/product/utils/productUtils";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { NotificationBell } from "@faire/web--source/slate/icons/foundation/NotificationBell";
import { NotificationBellChecked } from "@faire/web--source/slate/icons/foundation/NotificationBellChecked";
import { Row } from "@faire/web--source/slate/Layout/Row";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { observer } from "mobx-react";
import React from "react";
import styled, { css } from "styled-components";
var QuantitySelectorBase = function(param) {
    var productAddToCartState = param.productAddToCartState, product = param.product, subscribedBackInStockTokens = param.subscribedBackInStockTokens, allowCustomOption = param.allowCustomOption, containingModalElement = param.containingModalElement, deleteBackInStockNotification = param.deleteBackInStockNotification;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var selectedOption = productAddToCartState.selectedOption, selectedQuantity = productAddToCartState.selectedQuantity, selectedOptionLabel = productAddToCartState.selectedOptionLabel, productQuantityOptions = productAddToCartState.productQuantityOptions, perStyleOptionVariations = productAddToCartState.perStyleOptionVariations, currentStyleHasOnlyPrePacks = productAddToCartState.currentStyleHasOnlyPrePacks, hasBackorderNotification = productAddToCartState.hasBackorderNotification, minimumOrderQuantity = productAddToCartState.minimumOrderQuantity, unitMultiplier = productAddToCartState.unitMultiplier, isPrePackProductOption = productAddToCartState.isPrePackProductOption, emailWhenBackInStock = productAddToCartState.emailWhenBackInStock;
    if (selectedOption && !isProductOptionInStock(selectedOption, perStyleOptionVariations)) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(BackInStockButtonContent, {
                    hasBackorderNotification: hasBackorderNotification,
                    selectedOption: selectedOption,
                    subscribedBackInStockTokens: subscribedBackInStockTokens,
                    deleteBackInStockNotification: deleteBackInStockNotification,
                    emailWhenBackInStock: emailWhenBackInStock
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    size: 5
                })
            ]
        });
    }
    var isDiscontinued = isProductOptionDiscontinued(product, selectedOption);
    return /*#__PURE__*/ _jsxs("div", {
        style: {
            width: "100%"
        },
        tabIndex: 2,
        role: "button",
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                justify: "space-between",
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        children: quantityLabel(minimumOrderQuantity, unitMultiplier, isPrePackProductOption, strictLocalize)
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        "data-test-id": "".concat(currentStyleHasOnlyPrePacks ? "prepack-" : "", "product-case-qty"),
                        children: buildCaseQuantityText(getProductDetails(product), selectedOption, strictLocalize)
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: 4
            }),
            /*#__PURE__*/ _jsx(QuantityOrOptionPicker, {
                value: selectedQuantity === null || selectedQuantity === void 0 ? void 0 : selectedQuantity.toString(),
                onSelect: function(param) {
                    var value = param.value;
                    return productAddToCartState.setSelectedQuantity(parseInt(value));
                },
                options: productQuantityOptions,
                placeholder: isDiscontinued ? "0" : selectedOptionLabel,
                disabled: isDiscontinued,
                dataTestId: "QuantityOrOptionPicker",
                containingElement: containingModalElement,
                shouldUseCustomOption: allowCustomOption,
                getCustomOptionLabel: productAddToCartState.productOptionQuantityLabel
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "1x"
            })
        ]
    });
};
export var QuantitySelector = observer(QuantitySelectorBase);
var quantityLabel = function(minimumOrderQuantity, unitMultiplier, isPrePackProductOption, strictLocalize) {
    var label = isPrePackProductOption ? strictLocalize({
        id: "s8skad",
        defaultMessage: [
            {
                type: 0,
                value: "Pack Quantity"
            }
        ]
    }) : strictLocalize({
        id: "mwCjBr",
        defaultMessage: [
            {
                type: 0,
                value: "Item Quantity"
            }
        ]
    });
    if (minimumOrderQuantity !== unitMultiplier) {
        label += " " + strictLocalize({
            id: "i5b9+M",
            defaultMessage: [
                {
                    type: 0,
                    value: "(Minimum "
                },
                {
                    type: 1,
                    value: "minimumOrderQuantityNumber"
                },
                {
                    type: 0,
                    value: ")"
                }
            ]
        }, {
            minimumOrderQuantityNumber: minimumOrderQuantity
        });
    }
    return label;
};
var BackInStockButtonContent = function(param) {
    var hasBackorderNotification = param.hasBackorderNotification, selectedOption = param.selectedOption, subscribedBackInStockTokens = param.subscribedBackInStockTokens, emailWhenBackInStock = param.emailWhenBackInStock, deleteBackInStockNotification = param.deleteBackInStockNotification;
    var isSubscribedBackInStock = selectedOption !== undefined && selectedOption.token !== undefined && subscribedBackInStockTokens !== undefined && subscribedBackInStockTokens.includes(selectedOption.token);
    if (hasBackorderNotification || isSubscribedBackInStock) {
        return /*#__PURE__*/ _jsxs(OutOfStockEmail, {
            variant: "secondary",
            onClick: function() {
                return deleteBackInStockNotification();
            },
            "data-test-id": "notified-button-on-pdp",
            children: [
                /*#__PURE__*/ _jsx(StyledNotificationBellChecked, {}),
                /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "70oXEG",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "You\u2019ll be notified when restocked"
                        }
                    ]
                })
            ]
        });
    } else {
        return /*#__PURE__*/ _jsxs(OutOfStockEmail, {
            variant: "secondary",
            onClick: function() {
                return emailWhenBackInStock(true);
            },
            "data-test-id": "notify-me-button-on-pdp",
            children: [
                /*#__PURE__*/ _jsx(StyledNotificationBell, {}),
                /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "VEf+Ef",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Notify me when back in stock"
                        }
                    ]
                })
            ]
        });
    }
};
var NotifyMeIconStyles = css([
    "fontsize:15px;margin:7px;color:inherit;"
]);
var OutOfStockEmail = styled(Button).withConfig({
    componentId: "sc-b2a2757d-0"
})([
    "width:100%;"
]);
var StyledNotificationBellChecked = styled(NotificationBellChecked).withConfig({
    componentId: "sc-b2a2757d-1"
})([
    "",
    ";"
], NotifyMeIconStyles);
var StyledNotificationBell = styled(NotificationBell).withConfig({
    componentId: "sc-b2a2757d-2"
})([
    "",
    ";"
], NotifyMeIconStyles);
